import type {
  ContestPositionMap,
  ConferenceContest,
  ConferenceOption,
  ContestContent,
  OptionContent, 
  HandRaiseOrchestrator,
} from '@/types';

const formatContest = (contest: ConferenceContest): ContestContent & { optionsCount: number } => {
  const options: OptionContent[] = contest?.options?.map((option: ConferenceOption) => {
    if (option.parent === null) return formatOption(option);
  });

  const formatted: ContestContent & { optionsCount: number } = {
    reference: contest.reference,
    title: contest.title,
    mode: contest.mode,
    options: options,
    optionsCount: contest.optionsCount,
    customRulesets: contest.customRulesets,
    disregardVoterWeight: contest.disregardWeights,
    displayDescriptionOnSummary: contest.displayDescriptionOnSummary,
    markingType: {
      votesAllowedPerOption: contest.votesAllowedPerOption,
      blankSubmission: contest.blankOption,
      minMarks: contest.minimumVotes,
      maxMarks: contest.maximumVotes,
      maxPiles: contest.maxPiles,
      voteVariation: contest.ruleSet,
      encoding: {
        codeSize: 1,
        maxSize: 1,
        cryptogramCount: 1
      }
    },
    resultType: {
      name: contest.resultMethod
    },
  };

  if (contest.subtitle) formatted.subtitle = contest.subtitle;
  if (contest.question) formatted.question = contest.question;
  if (contest.description) formatted.description = contest.description;
  if (contest.collapsable) formatted.collapsable = contest.collapsable;
  if (contest.collapseDefault) formatted.collapseDefault = contest.collapseDefault;
  if (contest.searchForm) formatted.searchForm = contest.searchForm;
  if (contest.randomizeOptions) formatted.randomizeOptions = contest.randomizeOptions;
  if (contest.blankOptionColor) formatted.blankOptionColor = contest.blankOptionColor;

  return formatted;
}

const formatOption = (option: ConferenceOption): OptionContent => {
  const parsedOption: OptionContent = {
    reference: option.reference,
    code: option.code,
    title: option.title,
  };

  if (option.id) parsedOption.id = option.id;
  if (option.subtitle) parsedOption.subtitle = option.subtitle;
  if (option.description) parsedOption.description = option.description;
  if (option.image) parsedOption.image = option.image;
  if (option.selectable !== undefined) parsedOption.selectable = option.selectable;
  if (option.exclusive !== undefined) parsedOption.exclusive = option.exclusive;
  if (option.excluded !== undefined) parsedOption.excluded = option.excluded;
  if (option.position) parsedOption.position = option.position;
  if (option.randomizeChildren !== undefined) parsedOption.randomizeChildren = option.randomizeChildren;
  if (option.accentColor) parsedOption.accentColor = option.accentColor;
  if (option.url) parsedOption.url = option.url;
  if (option.videoUrl) parsedOption.videoUrl = option.videoUrl;
  if (option.minChooseableSuboptions) parsedOption.minChooseableSuboptions = option.minChooseableSuboptions;
  if (option.maxChooseableSuboptions) parsedOption.maxChooseableSuboptions = option.maxChooseableSuboptions;
  if (option.voteLimit) parsedOption.voteLimit = option.voteLimit;
  if (option.writeIn) {
    parsedOption.writeIn = {
      maxSize: option.writeInMaxSize,
      encoding: "utf8",
      placeholder: option.writeInPlaceholder,
    }
  }
  if (option.children) parsedOption.children = option.children.map(child => formatOption(child));

  return parsedOption;
}

const orderContests = (contestsSelection: ConferenceContest[], contestPositions: ContestPositionMap): ConferenceContest[] => {
  return contestsSelection.sort((a, b) =>
    contestPositions[a.reference] - contestPositions[b.reference]
  );
}

const orderResults = (contestsSelection: HandRaiseOrchestrator[], contestPositions: ContestPositionMap): HandRaiseOrchestrator[] => {
  return contestsSelection.sort((a, b) =>
    contestPositions[a.contestReference] - contestPositions[b.contestReference]
  );
}

const flattenOptions = (tree: ConferenceOption[]): ConferenceOption[] => {
  return tree.flatMap((option: ConferenceOption) => {
    if (option.children) return [option, ...flattenOptions(option.children)];
    return [option];
  });
}

export { formatContest, orderContests, flattenOptions, orderResults };
